import React from "react";
import classes from "./DonutChart.module.scss";

/**
 * ドーナツチャートのプロパティ
 */
type DonutChartProps = {
  /** 進捗率 (0.0 ～ 1.0) */
  progress: number;
};

/**
 * ドーナツチャート（進捗表示）
 * @param props ドーナツチャートのプロパティ
 */
const DonutChart: React.FC<DonutChartProps> = (props) => {
  /** ドーナツチャートの静的設定 */
  const donutSetting = {
    center: 50,
    outerRadius: 50,
    innerRadius: 40,
    animation: {
      rotate: "circle 1s 1 0.1s forwards ease-in-out",
      changeColor: "color 0s 1 1s forwards step-start",
    },
  };

  /**
   * ドーナツ部の半径を取得
   * @returns ドーナツ部の半径
   */
  const getDonutRadius = () => {
    const oR = donutSetting.outerRadius;
    const iR = donutSetting.innerRadius;
    return oR - (oR - iR) / 2;
  };
  const donutRadius = getDonutRadius();
  const donutRound = donutRadius * 2 * Math.PI;

  /**
   * ドーナツ部に適用するアニメーションを取得
   * @param round ドーナツの周の長さ
   * @returns アニメーションを定義した <style> 要素
   */
  const getAnimationStyle = (round: number) => {
    // 進捗に応じて keyframes を動的に制御する必要があるため、
    // TSX 側で <style> 要素を生成してセットする。
    return (
      <style>
        {`
          @keyframes circle {
            0% { stroke-dasharray: 0 ${round}; }
            100% { stroke-dasharray: ${round * props.progress} ${round}; }
          }
          @keyframes color {
            100% { stroke: orangered; }
          }
        `}
      </style>
    );
  };
  const customAnimation = getAnimationStyle(donutRound);

  /**
   * ドーナツ部のカスタムスタイル定義を取得
   * @param round ドーナツの周の長さ
   * @returns ドーナツ部のカスタムスタイル定義
   */
  const getCustomStyle = (round: number) => {
    // 進捗に応じてドーナツ部に適用するスタイルが動的に変化するため、
    // TSX 側でスタイルを適用する。

    const strokeWidth = donutSetting.outerRadius - donutSetting.innerRadius;
    const strokeOpacity = props.progress === 0 ? 0 : 1;

    const rotateAnim = donutSetting.animation.rotate;
    const completeColorChange =
      props.progress >= 1.0 ? `, ${donutSetting.animation.changeColor}` : "";

    return {
      animation: `${rotateAnim} ${completeColorChange}`,
      strokeDasharray: `0, 0, 0, ${round}`,
      strokeWidth,
      strokeOpacity,
    };
  };
  const customStyle = getCustomStyle(donutRound);

  return (
    <div className={classes.chartContainer}>
      {customAnimation}
      <svg width="100%" height="100%" viewBox="0 0 100 100">
        <circle
          className={classes.circle}
          r={donutSetting.outerRadius}
          cx={donutSetting.center}
          cy={donutSetting.center}
        ></circle>
        {props.progress >= 1.0 ? (
          // 達成時
          <React.Fragment>
            <circle
              className={classes.animationCircle}
              style={customStyle}
              r={donutRadius}
              cx={donutSetting.center}
              cy={donutSetting.center}
            ></circle>
            <circle
              className={classes.completedOverlay}
              r={donutSetting.innerRadius}
              cx={donutSetting.center}
              cy={donutSetting.center}
            ></circle>
          </React.Fragment>
        ) : (
          // 未達成時
          <React.Fragment>
            <circle
              className={classes.animationCircle}
              style={customStyle}
              r={donutRadius}
              cx={donutSetting.center}
              cy={donutSetting.center}
            ></circle>
            <circle
              className={classes.incompletedOverlay}
              r={donutSetting.innerRadius}
              cx={donutSetting.center}
              cy={donutSetting.center}
            ></circle>
          </React.Fragment>
        )}
      </svg>
    </div>
  );
};

export default DonutChart;
